import React, { useEffect } from 'react'
import type { ViewMode } from '../../types'
import {
  INTELLIGENCE_PROGRESS_PHASES,
  formatIntelligenceEta,
  getIntelligenceProgressLabel,
  getIntelligenceProgressPhase,
  getIntelligenceProgressState,
} from '../../lib/intelligenceProgressState'

type IntelligenceProgressOverlayProps = {
  viewMode: ViewMode
  query: string
  status?: string
  rawPhase?: string
  createdAt?: Date
  etaSeconds?: number
  stepLabel?: string
  isCancelling?: boolean
  onClose: () => void
  onCancel: () => void
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function ProgressRing({ progress }: { progress: number }) {
  const radius = 56
  const normalizedProgress = Math.max(0, Math.min(100, progress))
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (normalizedProgress / 100) * circumference

  return (
    <div className="mx-auto flex w-full max-w-[15rem] flex-col items-center">
      <div className="relative h-40 w-40">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 140 140" aria-hidden="true">
          <circle cx="70" cy="70" r={radius} stroke="currentColor" strokeWidth="8" className="text-gray-200 dark:text-gray-700" fill="none" />
          <circle
            cx="70"
            cy="70"
            r={radius}
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            className="text-blue-500"
            fill="none"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset,
              transition: 'stroke-dashoffset 250ms ease',
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-semibold tracking-tight text-gray-950 dark:text-white">{Math.round(normalizedProgress)}%</span>
          <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">Complete</span>
        </div>
      </div>
    </div>
  )
}

function getStatusPillLabel(status?: string): string {
  switch (status) {
    case 'queued':
      return 'Queued'
    case 'pending':
      return 'Starting'
    case 'interrupted':
      return 'Resuming'
    case 'podcast_pending':
      return 'Finalizing'
    default:
      return 'In Progress'
  }
}

export default function IntelligenceProgressOverlay({
  viewMode,
  query,
  status,
  rawPhase,
  createdAt,
  etaSeconds,
  stepLabel,
  isCancelling = false,
  onClose,
  onCancel,
}: IntelligenceProgressOverlayProps) {
  const { progress } = getIntelligenceProgressState({
    status,
    rawPhase,
    createdAt,
  })
  const activePhase = getIntelligenceProgressPhase({
    status,
    rawPhase,
    createdAt,
  })
  const summaryLabel = getIntelligenceProgressLabel({
    status,
    rawPhase,
    createdAt,
    stepLabel,
  })
  const activePhaseIndex = INTELLIGENCE_PROGRESS_PHASES.findIndex((phase) => phase.id === activePhase.id)
  const isMobile = viewMode === 'mobile'

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    const previousModalState = document.body.getAttribute('data-modal-open')

    document.body.style.overflow = 'hidden'
    document.body.setAttribute('data-modal-open', 'true')
    window.dispatchEvent(new Event('modal-open-changed'))

    return () => {
      document.body.style.overflow = previousOverflow
      if (previousModalState === null) {
        document.body.removeAttribute('data-modal-open')
      } else {
        document.body.setAttribute('data-modal-open', previousModalState)
      }
      window.dispatchEvent(new Event('modal-open-changed'))
    }
  }, [])

  const noticeClassName = isMobile
    ? 'rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3.5 text-sm font-medium text-blue-800 shadow-sm dark:border-blue-900/70 dark:from-blue-950/60 dark:to-indigo-950/50 dark:text-blue-200'
    : 'rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300'

  const cancelButtonClassName = isMobile
    ? 'inline-flex min-h-[52px] w-full items-center justify-center rounded-2xl border border-red-200 bg-red-50 px-5 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/40'
    : 'inline-flex min-h-[48px] items-center justify-center rounded-2xl border border-red-200 bg-red-50 px-5 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/40'

  const progressContent = (
    <>
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Close progress view"
          onClick={onClose}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="mt-6 text-center">
        <h2 className="text-3xl font-semibold tracking-tight text-gray-950 dark:text-white">Building Your Report</h2>
        <p className="mx-auto mt-3 max-w-xl text-base leading-7 text-gray-500 dark:text-gray-400">
          {query}
        </p>
      </div>

      <div className="mt-8">
        <ProgressRing progress={progress} />
      </div>

      <div className="mt-4 text-center">
        <p className="text-sm font-medium text-blue-600 dark:text-blue-400">{summaryLabel}</p>
        {etaSeconds && etaSeconds > 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Estimated time remaining: {formatIntelligenceEta(etaSeconds)}
          </p>
        ) : null}
      </div>

      <div className="mt-8 rounded-[28px] border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5">
        <div className="space-y-2">
          {INTELLIGENCE_PROGRESS_PHASES.map((phase, index) => {
            const isComplete = index < activePhaseIndex
            const isActive = phase.id === activePhase.id
            const isLast = index === INTELLIGENCE_PROGRESS_PHASES.length - 1

            return (
              <div key={phase.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold ${
                      isComplete
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : isActive
                          ? 'border-blue-500 bg-white text-blue-600 shadow-[0_0_0_4px_rgba(59,130,246,0.12)] dark:bg-gray-900 dark:text-blue-400'
                          : 'border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500'
                    }`}
                  >
                    {isComplete ? <CheckIcon /> : phase.phaseNumber}
                  </div>
                  {!isLast ? (
                    <div
                      className={`mt-2 h-10 w-px ${
                        isComplete ? 'bg-emerald-300 dark:bg-emerald-700' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  ) : null}
                </div>

                <div
                  className={`min-w-0 flex-1 rounded-2xl px-4 py-3 ${
                    isActive
                      ? 'bg-blue-500 text-white shadow-[0_16px_36px_rgba(59,130,246,0.28)]'
                      : 'bg-transparent text-gray-500 dark:text-gray-400'
                  }`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className={`text-lg font-semibold ${isActive ? 'text-white' : isComplete ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`}>
                      {phase.label}
                    </p>
                    {isActive ? (
                      <span className="rounded-full bg-white/18 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-white/95">
                        {getStatusPillLabel(status)}
                      </span>
                    ) : null}
                  </div>
                  <p className={`mt-1 text-sm leading-6 ${isActive ? 'text-blue-50' : 'text-gray-400 dark:text-gray-500'}`}>
                    {phase.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )

  if (isMobile) {
    return (
      <div
        data-testid="intelligence-progress-overlay"
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[110] bg-white/95 dark:bg-gray-950/95"
      >
        <div className="flex h-full flex-col bg-gray-50 dark:bg-gray-950">
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-2xl px-4 pb-6 pt-[calc(env(safe-area-inset-top,0px)+1rem)]">
              {progressContent}
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-200/80 bg-white/95 px-4 pt-4 shadow-[0_-16px_40px_rgba(15,23,42,0.10)] backdrop-blur dark:border-gray-800 dark:bg-gray-950/95">
            <div
              data-testid="intelligence-progress-mobile-actions"
              className="mx-auto max-w-2xl space-y-3 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)]"
            >
              <div className={noticeClassName}>
                We&apos;ll notify you when your report is ready.
              </div>

              <button
                type="button"
                onClick={onCancel}
                disabled={isCancelling}
                className={cancelButtonClassName}
              >
                {isCancelling ? 'Cancelling...' : 'Cancel Research'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="intelligence-progress-overlay"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[110] bg-white/95 dark:bg-gray-950/95"
    >
      <div className="h-full overflow-y-auto pl-24">
        <div className="min-h-full bg-gray-50/80 px-6 py-8 dark:bg-gray-950/80">
          <div
            className="mx-auto max-w-2xl rounded-[32px] border border-gray-200 bg-white shadow-[0_32px_80px_rgba(15,23,42,0.14)] dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="px-8 pb-8 pt-6">
              {progressContent}

              <div className={`mt-6 ${noticeClassName}`}>
                We&apos;ll notify you when your report is ready.
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={isCancelling}
                  className={cancelButtonClassName}
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel Research'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
